import { Box, InputAdornment, Stack, TextField } from '@mui/material';
import { format } from 'date-fns';
import { FC, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DATE_FORMAT_API } from '../../../../../../../../@constants/date';
import DateRangePicker from '../../../../../../../../components/DateRangePicker';
import { Calendar2Icon } from '../../../../../../../../components/SvgIcons/Calendar2Icon';
import { FilePdfIcon } from '../../../../../../../../components/SvgIcons/FilePdfIcon';
import { FooterExtraButtons } from '../../../../../../components/FooterButtons';
import { ReportLabel } from '../../../../../../components/ReportLabel';
import { RoomMoveReportFormModel } from '../../models';
import { UsersField } from './fields/UsersField';

export const FilterForm: FC<{
  onSubmit: (data: RoomMoveReportFormModel) => void;
  onReset: () => void;
}> = ({ onSubmit, onReset }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<RoomMoveReportFormModel>();

  const formValues = watch();

  const dates = useMemo(
    (): (Date | null)[] => [
      formValues?.from ? new Date(formValues?.from) : null,
      formValues?.to ? new Date(formValues?.to) : null,
    ],
    [formValues?.from, formValues?.to]
  );

  const handleChangeDates = (value: (Date | null)[]) => {
    const [from, to] = value;
    const _from = from ? format(from, DATE_FORMAT_API) : null;
    const _to = to ? format(to, DATE_FORMAT_API) : null;
    setValue('from', _from);
    setValue('to', _to);
  };

  return (
    <Box sx={{ p: 0, height: '100%', backgroundColor: 'colors.gray.o10', overflowY: 'scroll' }}>
      <Stack direction={'column'} spacing={0} sx={{ width: '100%', height: '100%' }}>
        <ReportLabel label="Room Move Report" />
        <Stack direction={'row'} gap={0} mt={2}>
          {/******************************** RIGHT BLOC ********************************/}
          <Box sx={{ flexGrow: 1, px: 3 }}>
            <Stack direction={'column'} spacing={2}>
              <DateRangePicker
                value={dates}
                onChange={handleChangeDates}
                maxDate={new Date()}
                renderInput={({ startProps, endProps }) => (
                  <Stack
                    direction="row"
                    spacing="1rem"
                    sx={{
                      width: '100%',
                    }}
                  >
                    <TextField
                      {...startProps}
                      size="medium"
                      variant="standard"
                      sx={{
                        width: '50%',
                      }}
                      label="From"
                      error={Boolean(errors?.from)}
                      helperText={errors?.from?.message}
                      placeholder="mm/dd/yyyy"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Calendar2Icon
                              sx={{
                                fontSize: '1rem',
                                color: ({ palette }) => palette.common.black,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      {...endProps}
                      size="medium"
                      variant="standard"
                      sx={{
                        width: '50%',
                      }}
                      label="To"
                      error={Boolean(errors?.to)}
                      helperText={errors?.to?.message}
                      placeholder="mm/dd/yyyy"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Calendar2Icon
                              sx={{
                                fontSize: '1rem',
                                color: ({ palette }) => palette.common.black,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                )}
              />
              <Controller
                control={control}
                name="users"
                render={({ field }) => (
                  <UsersField value={field.value} onChange={value => field.onChange(value)} />
                )}
              />

              <Box sx={{ height: 10 }} />

              <FooterExtraButtons
                onSubmit={file_type => {
                  setValue('file_type', file_type);
                  handleSubmit(onSubmit)();
                }}
                onReset={onReset}
                exportFileTypes={[
                  {
                    label: 'PDF',
                    value: 'pdf',
                    icon: <FilePdfIcon sx={{ width: '3rem', height: '3rem' }} />,
                  },
                ]}
              />
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};
